import { useState } from "react";
import { ShareEventButton } from "./ShareEventButton";
import { useShareEvent } from "@/hooks/use-share-event";
import { useToast } from "@/hooks/use-toast";
import { Event } from "@/types/event";
import { RecommendationModal } from "@/components/event-card/RecommendationModal";

interface EventViewFooterProps {
  event: Event;
  isHost: boolean;
}

export const EventViewFooter = ({ event, isHost }: EventViewFooterProps) => {
  const { shareEvent } = useShareEvent();
  const { toast } = useToast();
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  
  const hasDateRecommendations = 
    event.event_date_recommendations && 
    event.event_date_recommendations.length > 0;
  
  const shouldShowCTA = isHost && event.status === 'planned';
  
  const shouldShowConfirmDetails = shouldShowCTA && hasDateRecommendations;
  
  if (!shouldShowCTA || event.status === 'confirmed' || event.status === 'booked') {
    return null;
  }

  const handleShare = async () => {
    try {
      await shareEvent(event.event_id);
      toast({
        title: "Success",
        description: "RSVP link shared successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to share RSVP link",
        variant: "destructive"
      });
    }
  };
  
  const handleReviewRecommendations = () => {
    setShowRecommendationsModal(true);
  };
  
  const dateRecommendations = event.event_date_recommendations?.map(rec => ({
    recommendation_id: rec.recommendation_id,
    recommended_date: rec.recommended_date,
    is_selected: rec.is_selected,
    time_of_day: rec.time_of_day
  })) || [];
  
  const venueRecommendations = event.event_venue_recommendations?.map(rec => {
    if (!rec.venue) return null;
    return {
      recommendation_id: rec.recommendation_id,
      is_selected: rec.is_selected,
      venue: {
        name: rec.venue.name,
        address: rec.venue.address,
        image_url: rec.venue.image_url,
        style: rec.venue.style,
        price_range: rec.venue.price_range,
        website_url: rec.venue.website_url,
        instagram_handle: rec.venue.instagram_handle,
        google_maps_link: rec.venue.google_maps_link,
        venue_id: rec.venue.venue_id
      }
    };
  }).filter(Boolean) || [];
  
  return (
    <div className="fixed bottom-0 left-0 right-0 flex justify-center items-center py-4 bg-transparent">
      <div className="flex flex-col gap-3 w-full max-w-md px-4">
        {shouldShowConfirmDetails ? (
          <>
            <ShareEventButton 
              onClick={handleReviewRecommendations} 
              variant="primary" 
              buttonType="confirm"
              className="w-full"
            />
            <ShareEventButton
              onClick={handleShare}
              variant="secondary"
              buttonType="invite"
              className="w-full"
            />
            {showRecommendationsModal && (
              <RecommendationModal 
                isOpen={showRecommendationsModal} 
                onClose={() => setShowRecommendationsModal(false)} 
                eventId={event.event_id} 
                eventName={event.name} 
                dateRecommendations={dateRecommendations} 
                venueRecommendations={venueRecommendations} 
              />
            )}
          </>
        ) : (
          <ShareEventButton 
            onClick={handleShare} 
            variant="primary" 
            buttonType="invite"
            className="w-full"
          />
        )}
      </div>
    </div>
  );
};
